import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { track } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const getSessionId = () => {
  let id = localStorage.getItem('chat_session_id');
  if (!id) {
    id = 'chat_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('chat_session_id', id);
  }
  return id;
};

const SUGGESTIONS = ['How much does it cost?', 'How long does a website take?', 'Which sectors do you work with?'];
const GREETING = "Hi! I'm Cactus 🌵, ask me anything about our marketing, websites or pricing, and I'll point you in the right direction.";

const ChatWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: GREETING }]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [lead, setLead] = useState({ name: '', email: '', phone: '' });
  const [leadError, setLeadError] = useState('');
  const [leadLoading, setLeadLoading] = useState(false);
  const bodyRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, open, loading]);

  if (location.pathname.startsWith('/admin')) return null;

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: msg }]);
    setLoading(true);
    track('chatbot_message_sent');
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
      });
      const data = await res.json();
      setMessages((m) => [...m, { role: 'assistant', content: data.reply || "Sorry, I couldn't respond just now. Please email hello@bluecactus.digital." }]);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: "Sorry, I couldn't connect. Please email hello@bluecactus.digital or book a call at /book-online." }]);
    } finally {
      setLoading(false);
    }
  };

  const openChat = () => { setOpen(true); track('chatbot_opened'); };

  const submitLead = async () => {
    setLeadError('');
    if (!lead.name.trim() || !lead.email.trim() || !lead.phone.trim()) { setLeadError('Please enter your name, email and phone number.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email.trim())) { setLeadError('Please enter a valid email.'); return; }
    setLeadLoading(true);
    try {
      const res = await fetch(`${API}/api/chat/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId.current, name: lead.name.trim(), email: lead.email.trim(), phone: lead.phone.trim() }),
      });
      if (!res.ok) throw new Error('failed');
      setLeadDone(true);
      setLeadOpen(false);
      track('chatbot_lead_submitted');
      setMessages((m) => [...m, { role: 'assistant', content: `Thanks ${lead.name.trim().split(' ')[0]}! The team will be in touch at ${lead.email.trim()} very soon. In the meantime, feel free to ask me anything else.` }]);
    } catch {
      setLeadError('Something went wrong. Please try again or email hello@bluecactus.digital.');
    } finally {
      setLeadLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button data-testid="chat-fab" onClick={openChat} aria-label="Open chat"
          className="chat-fab"
          style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 950, width: 60, height: 60, borderRadius: '50%', background: 'var(--hot-pink)', color: '#fff', border: 'none', cursor: 'pointer', boxShadow: '0 6px 20px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform 0.2s ease' }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}>
          <MessageCircle size={28} />
        </button>
      )}

      {open && (
        <div data-testid="chat-panel" className="chat-panel"
          style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 951, width: 370, maxWidth: 'calc(100vw - 32px)', height: 540, maxHeight: 'calc(100vh - 120px)', background: '#fff', borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.28)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ background: 'var(--dark)', padding: '16px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--hot-pink)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><MessageCircle size={18} color="#fff" /></div>
              <div>
                <p style={{ fontFamily: 'var(--font-heading)', fontSize: 18, color: '#fff', margin: 0, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Chat with Cactus</p>
                <p style={{ fontSize: 11.5, color: 'var(--teal)', margin: 0 }}>Usually replies instantly</p>
              </div>
            </div>
            <button data-testid="chat-close" onClick={() => setOpen(false)} aria-label="Close chat" style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}><X size={20} /></button>
          </div>

          <div ref={bodyRef} data-testid="chat-messages" style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 8px', background: 'var(--off-white)' }}>
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
                <div data-testid={`chat-msg-${m.role}`} style={{ maxWidth: '82%', padding: '10px 14px', borderRadius: 12, fontSize: 18, lineHeight: 1.55, whiteSpace: 'pre-wrap',
                  background: m.role === 'user' ? 'var(--teal)' : '#fff', color: m.role === 'user' ? '#fff' : 'var(--dark)', border: m.role === 'user' ? 'none' : '1px solid #e2e8f0' }}>{m.content}</div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10 }}>
                <div style={{ padding: '10px 14px', borderRadius: 12, background: '#fff', border: '1px solid #e2e8f0', color: 'var(--body-text)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}><Loader2 size={14} className="spin" /> Typing…</div>
              </div>
            )}
            {messages.length <= 1 && !loading && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid="chat-suggestion" onClick={() => send(s)} style={{ background: '#fff', border: '1px solid var(--teal)', color: 'var(--teal)', borderRadius: 999, padding: '7px 12px', fontSize: 12.5, cursor: 'pointer' }}>{s}</button>
                ))}
              </div>
            )}
          </div>

          <div style={{ padding: 12, borderTop: '1px solid #e2e8f0', background: '#fff' }}>
            {!leadDone && !leadOpen && (
              <button data-testid="chat-lead-open" onClick={() => setLeadOpen(true)}
                style={{ width: '100%', marginBottom: 10, background: 'rgba(53,152,173,0.08)', border: '1px dashed var(--teal)', color: 'var(--teal)', borderRadius: 8, padding: '9px 12px', fontSize: 13, cursor: 'pointer', fontFamily: 'var(--font-heading)', letterSpacing: '0.02em' }}>
                📞 Want the team to contact you? Leave your details
              </button>
            )}
            {leadOpen && !leadDone && (
              <div data-testid="chat-lead-form" style={{ marginBottom: 10, padding: 12, background: 'var(--off-white)', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <p style={{ fontSize: 12.5, color: 'var(--body-text)', margin: '0 0 8px' }}>Leave your details and we'll be in touch, no obligation.</p>
                <input data-testid="chat-lead-name" value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Your name *" style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', fontSize: 13, marginBottom: 6, outline: 'none' }} />
                <input data-testid="chat-lead-email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Email *" style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', fontSize: 13, marginBottom: 6, outline: 'none' }} />
                <input data-testid="chat-lead-phone" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} placeholder="Phone *" style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px', fontSize: 13, marginBottom: 8, outline: 'none' }} />
                {leadError && <p data-testid="chat-lead-error" style={{ fontSize: 12, color: '#dc2626', margin: '0 0 8px' }}>{leadError}</p>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button data-testid="chat-lead-submit" onClick={submitLead} disabled={leadLoading} className="btn-pink-filled" style={{ flex: 1, justifyContent: 'center', fontSize: 12.5, padding: '9px 12px', opacity: leadLoading ? 0.6 : 1 }}>
                    {leadLoading ? <><Loader2 size={14} className="spin" /> Sending…</> : 'Request a callback'}
                  </button>
                  <button onClick={() => { setLeadOpen(false); setLeadError(''); }} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 8, padding: '9px 12px', fontSize: 12.5, cursor: 'pointer', color: 'var(--body-text)' }}>Cancel</button>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                placeholder="Type your question…" style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 18, outline: 'none' }} />
              <button data-testid="chat-send" onClick={() => send()} disabled={loading || !input.trim()} aria-label="Send"
                style={{ background: 'var(--hot-pink)', color: '#fff', border: 'none', borderRadius: 8, width: 44, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', opacity: loading || !input.trim() ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Send size={16} /></button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
